import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Divider,
  useTheme,
  Chip,
  useMediaQuery,
  IconButton,
  Collapse,
} from '@mui/material';
import {
  Dashboard,
  Restaurant,
  Store,
  MenuBook,
  LocalShipping,
  Event,
  TableRestaurant,
  Analytics,
  Settings,
  Home,
  // Payment, // Commented out - Payment tab is hidden
  QrCode,
} from '@mui/icons-material';

const menuItems = [
  {
    title: 'Home Page',
    icon: <Home sx={{ fontSize: '1.4rem' }} />,
    path: '/',
    description: 'Public Website',
    isPublic: true,
  },
  {
    title: 'Dashboard',
    icon: <Dashboard sx={{ fontSize: '1.4rem' }} />,
    path: '/admin/dashboard',
    description: 'Overview & Analytics',
  },
  {
    title: 'Configuration',
    icon: <Settings sx={{ fontSize: '1.4rem' }} />,
    path: '/admin/config',
    description: 'Restaurant Settings',
  },
  {
    title: 'Social Links',
    icon: <Settings sx={{ fontSize: '1.4rem' }} />,
    path: '/admin/social-links',
    description: 'Social Media Configuration',
  },
  {
    title: 'Outlets',
    icon: <Store sx={{ fontSize: '1.4rem' }} />,
    path: '/admin/outlets',
    description: 'Manage Locations',
  },
  {
    title: 'Menu',
    icon: <MenuBook sx={{ fontSize: '1.4rem' }} />,
    path: '/admin/menu',
    description: 'Food & Categories',
  },
  {
    title: 'Delivery',
    icon: <LocalShipping sx={{ fontSize: '1.4rem' }} />,
    path: '/admin/delivery',
    description: 'Orders & Takeaway',
    badge: 'New',
  },
  {
    title: 'Catering',
    icon: <Event sx={{ fontSize: '1.4rem' }} />,
    path: '/admin/catering',
    description: 'Event Services',
  },
  {
    title: 'Tables',
    icon: <TableRestaurant sx={{ fontSize: '1.4rem' }} />,
    path: '/admin/tables',
    description: 'Reservations & Orders',
  },
  {
    title: 'Table Orders',
    icon: <QrCode sx={{ fontSize: '1.4rem' }} />,
    path: '/admin/table-orders',
    description: 'QR Code Table Orders',
  },
  // {
  //   title: 'Payments',
  //   icon: <Payment sx={{ fontSize: '1.4rem' }} />,
  //   path: '/admin/payments',
  //   description: 'Payment Management',
  // },
  {
    title: 'Token Management',
    icon: <QrCode sx={{ fontSize: '1.4rem' }} />,
    path: '/admin/tokens',
    description: 'Token Settings',
  },
  {
    title: 'Analytics',
    icon: <Analytics />,
    path: '/admin/analytics',
    description: 'Reports & Insights',
    disabled: true,
  },
];

const Sidebar = ({ open, onClose, isMobile }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

  const handleNavigation = (path) => {
    // Check if navigating to a public page (not admin pages)
    if (path === '/' || 
        (path.startsWith('/') && !path.startsWith('/admin'))) {
      // Show confirmation dialog for public page navigation
      const confirmed = window.confirm(
        'You are about to navigate to the public website. This will log you out of the admin panel. Continue?'
      );
      
      if (!confirmed) {
        return; // User cancelled
      }
      
      // Clear authentication tokens when going to public pages
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      
      // Show a message to the user
      console.log('Navigating to public page - authentication tokens cleared');
    }
    
    navigate(path);
    if (isMobile) {
      onClose();
    }
  };

  const sidebarContent = (
    <Box
      sx={{
        width: open ? 280 : 80,
        height: '100%',
        bgcolor: 'background.paper',
        borderRight: '1px solid',
        borderColor: 'divider',
        backgroundImage: 'none',
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Logo Section */}
      <Box
        sx={{
          p: { xs: 2, sm: 3 },
          textAlign: 'center',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        {open ? (
          <Box>
            <Typography 
              variant="h5" 
              fontWeight="bold" 
              color="primary.main"
              sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
            >
              Restaurant
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
            >
              Admin Panel
            </Typography>
          </Box>
        ) : (
          <Restaurant sx={{ fontSize: { xs: 28, sm: 32 }, color: 'primary.main' }} />
        )}
      </Box>

      {/* Navigation Menu */}
      <Box sx={{ flexGrow: 1, py: { xs: 1, sm: 2 } }}>
        <List sx={{ px: { xs: 0.5, sm: 1 } }}>
          {menuItems.map((item, index) => {
            const isActive = location.pathname.startsWith(item.path);
            const isPublicPage = item.isPublic;
            
            return (
              <ListItem key={item.path} disablePadding sx={{ mb: { xs: 0.25, sm: 0.5 } }}>
                <ListItemButton
                  onClick={() => handleNavigation(item.path)}
                  disabled={item.disabled}
                  sx={{
                    borderRadius: 2,
                    mx: { xs: 0.5, sm: 1 },
                    minHeight: { xs: 48, sm: 56 },
                    bgcolor: isActive ? 'primary.main' : 'transparent',
                    color: isActive ? 'white' : 'text.primary',
                    transition: 'all 0.2s ease-in-out',
                    border: isPublicPage ? '1px solid' : 'none',
                    borderColor: isPublicPage ? 'warning.main' : 'transparent',
                    px: { xs: 1, sm: 2 },
                    py: { xs: 1, sm: 1.5 },
                    '&:hover': {
                      bgcolor: isActive ? 'primary.dark' : (isPublicPage ? 'warning.50' : 'action.hover'),
                      transform: 'translateX(4px)',
                    },
                    '&.Mui-disabled': {
                      opacity: 0.5,
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: 'inherit',
                      minWidth: open ? 40 : 'auto',
                      justifyContent: 'center',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  
                  {open && (
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1" fontWeight="500">
                            {item.title}
                          </Typography>
                          {item.badge && (
                            <Chip
                              label={item.badge}
                              size="small"
                              color="secondary"
                              sx={{ height: 20, fontSize: '0.9rem' }}
                            />
                          )}
                          {isPublicPage && (
                            <Chip
                              label="Public"
                              size="small"
                              color="warning"
                              sx={{ height: 20, fontSize: '0.9rem' }}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                          {item.description}
                        </Typography>
                      }
                      sx={{ m: 0 }}
                    />
                  )}
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>

        {open && (
          <Box sx={{ px: 2, mt: 2 }}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
              QUICK STATS
            </Typography>
            <Box sx={{ mt: 1, px: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  Today's Orders
                </Typography>
                <Typography variant="body2" fontWeight="600" color="primary.main">
                  24
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  Revenue
                </Typography>
                <Typography variant="body2" fontWeight="600" color="success.main">
                  S$1,245
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  Pending
                </Typography>
                <Typography variant="body2" fontWeight="600" color="warning.main">
                  3
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      {/* Footer */}
      {open && (
        <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary" align="center" display="block">
            Restaurant Management v1.0
          </Typography>
        </Box>
      )}
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={open}
        onClose={onClose}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          '& .MuiDrawer-paper': {
            width: 280,
            boxSizing: 'border-box',
          },
        }}
      >
        {sidebarContent}
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="permanent"
      sx={{
        '& .MuiDrawer-paper': {
          width: open ? 280 : 80,
          boxSizing: 'border-box',
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          overflowX: 'hidden',
        },
      }}
    >
      {sidebarContent}
    </Drawer>
  );
};

export default Sidebar;
